import { useEffect, useMemo, useRef, useState } from 'react'
import { applyHydratedSubWorkflows, collectUnhydratedSets, fetchWorkflowForProcessTree, hydrateProcessTree } from './processTreeHydration'
import { collectNightJobNodeIds } from './jobNightCharacter'

const EMPTY_HYDRATION = { subWorkflowsById: {}, failedSetNodeIds: [] }

/**
 * Story 183 (RN2, D14 · S14) — the census of the night job over the WHOLE workflow, nested subworkflows
 * included. Sun serves a subworkflow on read as a skeleton (id, code, name: `NodeDtoMapper`), so the jobs
 * of a set that has not been opened are not in the editor's model; without loading them, a night job
 * inside a subworkflow would not block the control of the rest of the workflow.
 *
 * Same technique, and same reason, as the composition census of #152 (`WorkflowFlow.jsx`): the sets are
 * loaded with the fault-tolerant hydrator of the Process Tree into a LOCAL cache by subworkflow id and
 * overlaid on the live model on every render. Nothing is written into the reducer: what is loaded here
 * would otherwise travel in the save as a user change. A set that fails to load contributes nothing and
 * is not asked for again; Sun still validates RN2 on save (V1, E-S1).
 *
 * With the feature inactive nothing is requested at all (RN21, S88): the census is empty.
 *
 * @param {Object} workflowData The editor's reducer state (a new object on every dispatch)
 * @param {boolean} enabled C4, `frontEndConfig.nightJobEnabled`
 * @returns {string} The ids of the marked job nodes, joined by `|` — a value, so it can key a memo
 */
export const useNightJobCensusKey = (workflowData, enabled) => {
  const [hydration, setHydration] = useState(EMPTY_HYDRATION)
  const hydratingRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Shallow copy on purpose: with an empty cache `applyHydratedSubWorkflows` returns its input as is, and
  // the reducer patches that object in place, so its identity would never change (see WorkflowFlow.jsx).
  const censusWorkflow = useMemo(
    () => (enabled ? applyHydratedSubWorkflows({ ...workflowData?.workflow }, hydration.subWorkflowsById) : {}),
    [enabled, workflowData, hydration.subWorkflowsById]
  )

  useEffect(() => {
    if (!enabled || hydratingRef.current) {
      return
    }
    const pending = collectUnhydratedSets(censusWorkflow?.nodes).filter((entry) =>
      !hydration.subWorkflowsById[entry.subWorkflowId] && !hydration.failedSetNodeIds.includes(entry.nodeId))
    if (pending.length === 0) {
      return
    }
    hydratingRef.current = true
    hydrateProcessTree(censusWorkflow, fetchWorkflowForProcessTree, hydration.subWorkflowsById).then((result) => {
      hydratingRef.current = false
      if (!mountedRef.current) {
        return
      }
      setHydration((previous) => ({
        subWorkflowsById: { ...previous.subWorkflowsById, ...result.subWorkflowsById },
        failedSetNodeIds: [...new Set([...previous.failedSetNodeIds, ...result.failedSetNodeIds])]
      }))
    })
  }, [enabled, censusWorkflow, hydration])

  return enabled ? collectNightJobNodeIds(censusWorkflow).join('|') : ''
}

export default useNightJobCensusKey
